import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ciq_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Request setup error
    return Promise.reject({ message: 'Request setup failed', name: 'RequestError' });
  }
);

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || '';
    const config = err?.config;

    // Auto-retry on 429 (rate-limit) with exponential backoff.
    // The ingress layer briefly bursts 429s when several parallel requests fire
    // during a page navigation. A short 250ms → 500ms → 1s retry recovers
    // silently instead of leaving the UI with empty data.
    if (status === 429 && config && !config.__isRetry) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= 3) {
        const delay = 250 * Math.pow(2, config.__retryCount - 1);
        await new Promise((r) => setTimeout(r, delay));
        config.__isRetry = config.__retryCount >= 3;   // last attempt, stop retrying after
        return api.request(config);
      }
    }

    // Handle 401 Unauthorized
    if (status === 401) {
      localStorage.removeItem('ciq_token');
      localStorage.removeItem('ciq_user');
      // Public routes that must remain accessible without a session
      const p = window.location.pathname;
      const isPublic = p === '/' || p.startsWith('/mobile/') || p.startsWith('/licences/');
      if (!isPublic) {
        window.location.href = '/';
      }
    }

    // Trial expired or account deactivated → sign user out and redirect to a friendly page.
    if (status === 403 && /trial has expired|account is no longer active/i.test(detail)) {
      try {
        localStorage.removeItem('ciq_token');
        localStorage.removeItem('ciq_refresh_token');
        localStorage.removeItem('ciq_user');
        sessionStorage.setItem('ciq_trial_expired', '1');
      } catch (_) { /* ignore */ }
      if (!window.location.pathname.startsWith('/trial-expired')) {
        window.location.href = '/trial-expired';
      }
    }

    // Create a clean error object to avoid circular reference issues
    try {
      const cleanError = new Error(err?.message || 'Network error');
      cleanError.name = err?.name || 'Error';
      cleanError.code = err?.code;
      if (err?.response) {
        cleanError.response = {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        };
      }
      return Promise.reject(cleanError);
    } catch (e) {
      // Fallback if even error extraction fails
      return Promise.reject(new Error('Request failed'));
    }
  }
);

// Authenticated window.fetch. Used by modules that stream/poll with fetch rather
// than axios; without the bearer the model URN guard withholds the model identifier.
export const authFetch = (url, init = {}) => {
  const token = localStorage.getItem('ciq_token');
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...init, headers });
};

export default api;
